// firebase
import firebase from "firebase/compat";
import {
  getDatabase,
  set,
  ref,
  get,
  onValue,
  update,
  child,
  push,
  remove,
  query,
  limitToFirst,
  startAt,
  orderByChild,
  equalTo,
  onDisconnect,
  serverTimestamp,
} from "firebase/database";
// commons code
import { isEmpty } from "../../../common/utils";
import { REALTIME_PATH } from "../../../config/config.fireabse.realtime";
import { ROUTE_PATH } from "../../../config/CONSTANTS";

const safeObjectData = (data) => {
  const safeData = {};
  if (data) {
    Object.entries(data).forEach(([key, value]) => {
      safeData[key] = value;
    });
  }
  return safeData;
};

const safeWriteDataObj = (data) => {
  // 過濾 value 為 undefined 的 key
  const safeData = {};
  if (data) {
    Object.entries(data).forEach(([key, value]) => {
      // value 可能是 true/false,但不可為 null
      if (value !== null) {
        safeData[key] = value;
        // recursively check
        if (typeof safeData[key] === "object") {
          safeData[key] = safeWriteDataObj(safeData[key]);
        }
      }
    });
  }
  return safeData;
};

const fireDb = {};

// get user from realtime database
fireDb.getUserOnce = ({ uid }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}`);

  return get(userRef)
    .then((snapshot) => {
      let data = {};
      if (snapshot.exists()) {
        const snapshotVal = snapshot.val();
        data = safeObjectData(snapshotVal);
      }
      return data;
    })
    .catch((err) => err);
};

// getUserRt is better than getUserOnce for performance
fireDb.getUserSettingOnce = ({ uid }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;

  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}/settings`);
  return get(userRef)
    .then((snapshot) => {
      let data = {};
      if (snapshot.exists()) {
        const snapshotVal = snapshot.val();
        data = safeObjectData(snapshotVal);
      }
      return data;
    })
    .catch((err) => err);
};

// getUserRt is better than getUserOnce for performance
fireDb.getUserRt = ({ uid, onSuccess, onNoData }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}`);
  onValue(userRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      onSuccess(safeObjectData(snapshotVal));
    } else if (onNoData) onNoData();
  });
};

// getUserRt is better than getUserOnce for performance
fireDb.getUserInfoRt = ({ uid, onSuccess, onNoData }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}/info`);
  onValue(userRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      onSuccess(safeObjectData(snapshotVal));
    } else if (onNoData) onNoData();
  });
};

// getUserRt is better than getUserOnce for performance
fireDb.getUserGroupsRt = ({ uid, onSuccess, onNoData }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}/groups`);
  onValue(userRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      const inGroups = Object.entries(snapshotVal || {})
        .filter(([key, obj]) => obj.isMember)
        // 區分正式站/測試站
        .filter(
          ([key, obj]) => obj.appWebMode === process.env.REACT_APP_WEB_MODE
        );
      if (inGroups.length === 0) {
        onNoData();
      } else {
        onSuccess(safeObjectData(snapshotVal));
      }
    } else if (onNoData) onNoData();
  });
};

// getUserRt is better than getUserOnce for performance
fireDb.getUserFilesRt = ({ uid, onSuccess, onNoData }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}/files`);
  onValue(userRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      onSuccess(safeObjectData(snapshotVal));
    } else if (onNoData) onNoData();
  });
};

// getUserRt is better than getUserOnce for performance
fireDb.getUserSettingRt = ({ uid, onSuccess, onNoData }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}/settings`);
  onValue(userRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      onSuccess(safeObjectData(snapshotVal));
    } else if (onNoData) onNoData();
  });
};

fireDb.getUsersByIds = (userIds = [], onSuccess, onError) => {
  const allQuery = [];
  userIds.forEach((id) => {
    allQuery.push(fireDb.getUserOnce({ uid: id }));
  });
  Promise.all(allQuery)
    .then((res) => {
      const users = res.map((cur) => ({
        uid: cur?.info?.uid,
        displayName: cur?.info?.displayName,
        groups: cur?.groups,
      }));
      if (onSuccess) onSuccess(users);
    })
    .catch((err) => {
      if (onError) onError(err);
    });
};

fireDb.findUsersByEmail = ({ email, onSuccess, onError }) => {
  const db = getDatabase();

  const emailRef = query(
    ref(db, REALTIME_PATH.users.root),
    orderByChild("info/email"),
    equalTo(email)
  );

  // eslint-disable-next-line consistent-return
  return onSuccess([]);

  // fixme: 下面會造成 query 多次,以致重複建立 預設 group
  // get(emailRef)
  //   .then((snapshot) => {
  //     let data = {};
  //     if (snapshot.exists()) {
  //       const snapshotVal = snapshot.val();
  //       data = safeObjectData(snapshotVal);
  //     }
  //     if (onSuccess) onSuccess(data);
  //   })
  //   .catch((err) => {
  //     if (onError) onError(err);
  //   });
};

// create user
fireDb.createUser = ({ uid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}`);

  set(userRef, safeWriteDataObj({ info: data.info }))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      if (onError) onError(err);
    });
};

// update user
fireDb.updateUser = ({ uid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}`);

  update(userRef, safeWriteDataObj(data))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// update user
fireDb.updateUserRole = ({ uid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}/info`);

  update(userRef, safeWriteDataObj(data))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// update user
fireDb.updateUserSetting = ({ uid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}/settings`);

  update(userRef, safeWriteDataObj(data))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// update user
fireDb.updateUserInfo = ({ uid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}`);

  update(userRef, safeWriteDataObj({ info: data }))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// update users.[uid].groups.[groupId]
fireDb.updateUserGroups = ({ userId, groupId, data, onSuccess, onError }) => {
  const db = getDatabase();
  // make sure uid is correct
  const safeUid = isEmpty(userId) ? "unknownUid" : userId;
  const safeUid2 = isEmpty(groupId) ? "unknownUid" : groupId;
  const userGroupsRef = ref(
    db,
    `${REALTIME_PATH.users.path}${safeUid}/groups/${safeUid2}`
  );

  update(userGroupsRef, safeObjectData(data))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// update users.[uid].groups.[groupId]
fireDb.deleteUserGroup = ({ userId, groupId, onSuccess, onError }) => {
  const db = getDatabase();
  // make sure uid is correct
  const safeUid = isEmpty(userId) ? "unknownUid" : userId;
  const safeUid2 = isEmpty(groupId) ? "unknownUid" : groupId;
  const userGroupsRef = ref(
    db,
    `${REALTIME_PATH.users.path}${safeUid}/groups/${safeUid2}`
  );

  remove(userGroupsRef)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// 取得單一 group 資訊
fireDb.getFile = ({ uid }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;

  const fileRef = ref(
    db,
    `${REALTIME_PATH.dataset.path}/${process.env.REACT_APP_WEB_MODE}/${safeUid}`
  );
  return new Promise((resolve, reject) => {
    onValue(fileRef, (snapshot) => {
      const snapshotVal = snapshot.val();
      if (!snapshotVal) resolve(null);
      resolve(safeObjectData(snapshotVal));
    });
  });
};

fireDb.getFilesInfoByIds = (fileIds = [], onSuccess, onError) => {
  const allQuery = [];
  fileIds.forEach((id) => {
    allQuery.push(fireDb.getFile({ uid: id }));
  });
  Promise.all(allQuery)
    .then((res) => {
      const files = [...(res || [])].filter((item) => !!item);
      if (onSuccess) onSuccess(files);
    })
    .catch((err) => {
      if (onError) onError(err);
    });
};

// create or update or delete user files
fireDb.updateUserFiles = ({ userId, fileUid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUserUid = isEmpty(userId) ? "unknownUid" : userId;

  let fileRef;
  let curFileUid;
  if (fileUid) {
    // 使用原本的 fileUid
    fileRef = ref(
      db,
      `${REALTIME_PATH.users.path}${safeUserUid}/files/${fileUid}`
    );
    curFileUid = fileUid;
  } else {
    const userFilesRef = ref(
      db,
      `${REALTIME_PATH.users.path}${safeUserUid}/files`
    );
    // 自動建立新的 fileId
    fileRef = push(userFilesRef);
    curFileUid = fileRef.key;
  }

  const safeData = safeWriteDataObj({
    ...(data || {}),
    uid: curFileUid,
    isOwner:
      data?.isOwner === null || data?.isOwner === undefined
        ? true
        : data.isOwner,
    // 區分正式站/測試站
    appWebMode: process.env.REACT_APP_WEB_MODE,
  });

  update(fileRef, safeData)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// create or update or delete user files
fireDb.updateFileDisplayName = ({ fileUid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeFileUid = isEmpty(fileUid) ? "unknownUid" : fileUid;

  // 使用原本的 fileUid
  const fileRef = ref(
    db,
    `${REALTIME_PATH.dataset.path}/${process.env.REACT_APP_WEB_MODE}/${safeFileUid}`
  );

  update(fileRef, { displayName: data.displayName })
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.updateFileGroup = ({ fileUid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeFileUid = isEmpty(fileUid) ? "unknownUid" : fileUid;

  // 使用原本的 fileUid
  const fileRef = ref(
    db,
    `${REALTIME_PATH.dataset.path}/${process.env.REACT_APP_WEB_MODE}/${safeFileUid}/groupowner`
  );

  // data: {groupId:true}  / {groupId:false}
  update(fileRef, data)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// create or update or delete single group files
fireDb.updateGroupSingleFile = ({
  groupId,
  fileUid,
  data,
  onSuccess,
  onError,
}) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeGroupUid = isEmpty(groupId) ? "unknownUid" : groupId;

  let fileRef;
  let curFileUid;
  if (fileUid) {
    // 使用原本的 fileUid
    fileRef = ref(
      db,
      `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeGroupUid}/files/${fileUid}`
    );
    curFileUid = fileUid;
  } else {
    const userFilesRef = ref(
      db,
      `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeGroupUid}/files`
    );
    // 自動建立新的 fileId
    fileRef = push(userFilesRef);
    curFileUid = fileRef.key;
  }

  const safeData = safeWriteDataObj({
    ...data,
    uid: curFileUid,
    isOwner:
      data?.isOwner === null || data?.isOwner === undefined
        ? true
        : data.isOwner,
  });

  // data: {fileUid:{isOwner: true, uid: fileUid}}  /
  update(fileRef, safeData)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// create or update or delete single group files
fireDb.deleteGroupSingleFile = ({
  groupId,
  fileUid,
  data,
  onSuccess,
  onError,
}) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeGroupUid = isEmpty(groupId) ? "unknownUid" : groupId;

  // 使用原本的 fileUid
  const fileRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeGroupUid}/files/${fileUid}`
  );

  // data: {fileUid:{isOwner: true, uid: fileUid}}  /
  remove(fileRef)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// create or update or delete group files
fireDb.updateGroupFilesMulti = ({ groupId, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeGroupUid = isEmpty(groupId) ? "unknownUid" : groupId;

  const filesRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeGroupUid}/files`
  );

  const safeData = safeWriteDataObj(data);
  // safeData:
  // {fileuid1:{isOwner:true,uid:fileuid1}, fileuid2:{isOwner:true,uid:fileuid2}}
  update(filesRef, safeData)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// create or update dataset
fireDb.creatDataset = ({ userId, groupId, data, onSuccess, onError }) => {
  const db = getDatabase();

  const datasetRef = ref(
    db,
    `${REALTIME_PATH.dataset.path}/${process.env.REACT_APP_WEB_MODE}`
  );
  // 自動建立新的 fileId
  const newFileRef = push(datasetRef);
  const curFileUid = newFileRef.key;

  const safeData = safeWriteDataObj({
    ...data,
    uid: curFileUid,
  });
  if (userId) {
    safeData.userowner = {};
    safeData.userowner[userId] = true;
  }
  if (groupId) {
    safeData.groupowner = {};
    safeData.groupowner[groupId] = true;
  }

  set(newFileRef, safeData)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess(curFileUid);
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// 只更新 dataset userowner/ groupowner
fireDb.updateDatasetOwner = () => {};

// update user connection status
fireDb.updateUserConnectStatus = ({
  uid,
  onConnect,
  data,
  onSuccess,
  onError,
}) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}`);
  const updateData = {
    ...data,
    onConnect,
    lastOnline: serverTimestamp(),
  };

  update(userRef, safeWriteDataObj(updateData))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// 取得單一 group 資訊
fireDb.getGroupRt = ({ uid }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;

  // console.log("[getGroupRt]safeUid", safeUid);

  const userRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeUid}`
  );
  return new Promise((resolve, reject) => {
    onValue(userRef, (snapshot) => {
      const snapshotVal = snapshot.val();
      // console.log("snapshotVal", snapshotVal);
      if (!snapshotVal) resolve(null);
      resolve(safeObjectData(snapshotVal));
    });
  });
};

// getUserRt is better than getUserOnce for performance
fireDb.getGroupFilesRt = ({ uid, onSuccess, onNoData }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeUid}/files`
  );
  onValue(userRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      onSuccess(safeObjectData(snapshotVal));
    } else if (onNoData) onNoData();
  });
};

// 依據 groupIds 取得 groups 資訊
fireDb.getGroupsByIds = (groupIds = [], onSuccess, onError) => {
  const allQuery = [];
  groupIds.forEach((id) => {
    allQuery.push(fireDb.getGroupRt({ uid: id }));
  });
  Promise.all(allQuery)
    .then((res) => {
      // console.log("[getGroupsByIds] res", res);
      const groups = [...(res || [])].filter((item) => !!item);
      // console.log("[getGroupsByIds] groups", groups);
      if (onSuccess) onSuccess(groups);
    })
    .catch((err) => {
      if (onError) onError(err);
    });
};

// for root  user
fireDb.getGroups = () => {
  const db = getDatabase();

  const groupsPath = REALTIME_PATH.groups.root;
  const targetRef = ref(db, `${groupsPath}/${process.env.REACT_APP_WEB_MODE}`);
  onValue(targetRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    return safeObjectData(snapshotVal);
  });
};

// 取得使用者在各 group 中的 role
fireDb.getUserRoleGroupInfoInGroups = ({
  userId,
  groupIds = [],
  onSuccess,
  onError,
}) => {
  const handleSuccess = (groups) => {
    const newData = groupIds.map((groupId, idx) => ({
      groupId,
      role: groups?.[idx]?.members?.[userId]?.role,
      displayName: groups?.[idx]?.displayName,
      files: Object.entries(groups?.[idx]?.files || {})
        .filter(([fileuid, fileObjVal]) => fileObjVal.isOwner)
        .map(([fileuid, fileObjVal]) => fileObjVal),
      // .reduce((acc, [fileuid, fileObjVal]) => {
      //   acc[fileuid] = fileObjVal;
      //   return acc;
      // }, {}), // [{isOwner:true,uid:"fileuid1"},{isOwner:true,uid:"fileuid2"}]
    }));
    if (onSuccess) onSuccess(newData);
  };
  const handleError = (err) => {
    if (onError) onError(err);
  };
  fireDb.getGroupsByIds(groupIds, handleSuccess, handleError);
};

fireDb.findGroupByGroupName = (keyword) => {
  const db = getDatabase();

  const groupsPath = REALTIME_PATH.groups.root;
  const targetRef = query(
    ref(db, `${groupsPath}/${process.env.REACT_APP_WEB_MODE}`),
    equalTo(keyword)
  );
  return new Promise((resolve, reject) => {
    onValue(targetRef, (snapshot) => {
      const snapshotVal = snapshot.val();
      if (!snapshotVal) resolve(null);
      resolve(safeObjectData(snapshotVal));
    });
  });
};

fireDb.createGroup = ({ data, onSuccess, onError }) => {
  const db = getDatabase();

  const groupListRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}`
  );
  // 自動建立新的 groupId

  const newGroupRef = push(groupListRef);
  const newGroupKey = newGroupRef.key;

  set(
    newGroupRef,
    safeObjectData({
      uid: newGroupKey,
      ...data,
    })
  )
    .then(() => {
      // Data saved successfully!
      // 傳送 groupKey, 做後續處理
      if (onSuccess) onSuccess(newGroupKey);
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.createGroupToUserGroups = ({
  userId,
  groupId,
  data,
  onSuccess,
  onError,
}) => {
  const db = getDatabase();

  // 自動建立新的 groupId
  const safeUid = isEmpty(userId) ? "unknownUid" : userId;
  const safeUid2 = isEmpty(groupId) ? "unknownUid" : groupId;
  const targetRef = ref(
    db,
    `${REALTIME_PATH.users.path}${safeUid}/groups/${safeUid2}`
  );

  update(
    targetRef,
    safeObjectData({
      ...data,
    })
  )
    .then(() => {
      // Data saved successfully!
      // 傳送 groupKey, 做後續處理
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.updateGroup = ({ uid, data, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const groupRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeUid}`
  );

  update(groupRef, safeObjectData(data))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.updateGroupMember = ({ userId, groupId, data, onSuccess, onError }) => {
  const db = getDatabase();
  // make sure uid is correct
  const safeUid = isEmpty(groupId) ? "unknownUid" : groupId;
  const safeUid2 = isEmpty(userId) ? "unknownUid" : userId;
  const groupUserRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeUid}/members/${safeUid2}`
  );

  update(groupUserRef, safeObjectData(data))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.removeGroupMember = ({ userId, groupId, data, onSuccess, onError }) => {
  const db = getDatabase();
  // make sure uid is correct
  const safeUid = isEmpty(groupId) ? "unknownUid" : groupId;
  const safeUid2 = isEmpty(userId) ? "unknownUid" : userId;
  const groupUserRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeUid}/members/${safeUid2}`
  );

  remove(groupUserRef)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.deleteGroup = ({ groupId, onSuccess, onError }) => {
  const db = getDatabase();
  // make sure uid is correct
  const safeUid = isEmpty(groupId) ? "unknownUid" : groupId;
  const groupRef = ref(
    db,
    `${REALTIME_PATH.groups.path}${process.env.REACT_APP_WEB_MODE}/${safeUid}`
  );

  remove(groupRef)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

// data: {path1: data, path2: data}
// e.g. {"/users/11111": userData, "/groups/123874182374": groupData}
// update multi data to realtime database
fireDb.updateMulti = ({ data, onSuccess, onError }) => {
  const db = getDatabase();

  // update 可以實現多個地方的寫入
  const updates = { ...data };

  // todo:check data structure

  update(ref(db), updates)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.deleteUser = ({ uid, onSuccess, onError }) => {
  const db = getDatabase();

  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userRef = ref(db, `${REALTIME_PATH.users.path}${safeUid}`);

  // todo:check data structure

  // 使用 update 取代 remove
  update(userRef, null)
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess();
    })
    .catch((err) => {
      // The write failed...
      if (onError) onError(err);
    });
};

fireDb.updateUserConnections = ({ uid }) => {
  // Since I can connect from multiple devices or browser tabs, we store each connection instance separately
  // any time that connectionsRef's value is null (i.e. has no children) I am offline
  const db = getDatabase();
  // make sure uid is correct
  const safeUid = isEmpty(uid) ? "unknownUid" : uid;
  const userConnectionsRef = `${REALTIME_PATH.users.path}${safeUid}/connections`;

  const myConnectionsRef = ref(db, userConnectionsRef);

  // stores the timestamp of my last disconnect (the last time I was seen online)
  const lastOnlineRef = ref(
    db,
    `${REALTIME_PATH.users.path}/${safeUid}/lastOnline`
  );

  const connectedRef = ref(db, ".info/connected");

  // fixme: 移除舊的 connection 資料
  // onValue(connectedRef, (snap) => {
  //   if (snap.val() === true) {
  //     // We're connected (or reconnected)! Do anything here that should happen only if online (or on reconnect)
  //     const con = push(myConnectionsRef);
  //
  //     // When I disconnect, remove this device
  //     onDisconnect(con).remove();
  //
  //     // Add this device to my connections list
  //     // this value could contain info about the device or a timestamp too
  //     set(con, true);
  //
  //     // When I disconnect, update the last time I was seen online
  //     onDisconnect(lastOnlineRef).set(serverTimestamp());
  //   }
  // });
};

// handle connection is on or off
// 可以用來改變 user 的狀態
fireDb.handleConnection = ({ onConnect, onDisConnect }) => {
  const db = getDatabase();
  // Firebase Realtime Database provides a special location at /.info/connected
  const connectedRef = ref(db, ".info/connected");
  onValue(connectedRef, (snap) => {
    if (snap.val() === true) {
      if (onConnect) onConnect();
    } else if (onDisConnect) onDisConnect();
  });
};

// 使用 update, 不會覆蓋其他子節點
fireDb.creatLogError = ({ data, onSuccess, onError }) => {
  const db = getDatabase();
  // 自動建立新的 logErrorId
  const logErrorRef = ref(
    db,
    `${REALTIME_PATH.errorLog.path}${process.env.REACT_APP_WEB_MODE}`
  );
  const newLogErrorRef = push(logErrorRef);
  const newLogErrorKey = newLogErrorRef.key;

  // <data>: {
  // logFrom:   log 來源 domain
  // code:      log 代碼
  // message:   log 訊息
  // userName:  使用者
  // time:      發生時間
  // info:      其他資訊
  // operation: 使用者操作細節
  // classification:
  // }

  const logData = {
    ...(data || {}),
    logFrom: process.env.REACT_APP_WEB_DOMAIN,
    time: data.time || Date.now(), // 發生錯誤的時間
    key: newLogErrorKey,
  };

  set(newLogErrorRef, safeWriteDataObj(logData))
    .then(() => {
      // Data saved successfully!
      if (onSuccess) onSuccess(newLogErrorKey);
    })
    .catch((err) => {
      if (onError) onError(err);
    });
};

fireDb.getPublicDatasetRt = ({ onSuccess, onNoData }) => {
  const db = getDatabase();

  const diaryPath = REALTIME_PATH.diary.root;
  const targetRef = ref(db, `${diaryPath}`);
  onValue(targetRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      onSuccess(safeObjectData(snapshotVal));
    } else if (onNoData) onNoData();
  });
};

// once get modules setting
fireDb.getModulesOnce = () => {
  const db = getDatabase();

  // make sure uid is correct
  const modulesRef = ref(
    db,
    `${REALTIME_PATH.modules.root}/${process.env.REACT_APP_WEB_MODE}`
  );
  return get(modulesRef)
    .then((snapshot) => {
      let data = {};
      if (snapshot.exists()) {
        const snapshotVal = snapshot.val();
        data = safeObjectData(snapshotVal);
      }
      return data;
    })
    .catch((err) => err);
};

// realtime get modules setting
fireDb.getModulesRt = ({ onSuccess, onNoData }) => {
  const db = getDatabase();

  // make sure uid is correct
  const modulesRef = ref(
    db,
    `${REALTIME_PATH.modules.root}/${process.env.REACT_APP_WEB_MODE}`
  );

  onValue(modulesRef, (snapshot) => {
    const snapshotVal = snapshot.val();
    if (!isEmpty(snapshotVal)) {
      onSuccess(safeObjectData(snapshotVal));
    } else if (onNoData) onNoData();
  });
};

// update user
fireDb.updateEmailsLinkUser = ({ onSuccess, onError }) => {
  const db = getDatabase();

  const userRef = ref(db, `${REALTIME_PATH.users.path}`);

  return get(userRef)
    .then((snapshot) => {
      let data = {};
      if (snapshot.exists()) {
        const snapshotVal = snapshot.val();
        data = safeObjectData(snapshotVal);
      }
      const safeKey = (str) => str.replaceAll(".", "_");
      const emailUserData = Object.entries(data).reduce(
        (acc, [uid, userObj]) => {
          if (userObj?.info?.email) {
            acc[safeKey(userObj?.info?.email)] = uid;
          }
          return acc;
        },
        {}
      );
      const emailRef = ref(db, REALTIME_PATH.email.path);

      update(emailRef, safeWriteDataObj(emailUserData))
        .then(() => {
          // Data saved successfully!
          if (onSuccess) onSuccess();
        })
        .catch((err) => {
          // The write failed...
          if (onError) onError(err);
        });
    })
    .catch((err) => {
      if (onError) onError(err);
    });
};

export default fireDb;
